<template>
  <div
    class="flex flex-col justify-center mb-2 bg-white rounded-xl border border-gray-300 border-solid"
  >
    <div class="flex flex-col justify-center w-full rounded-xl">
      <div
        class="flex gap-10 justify-between items-center px-6 py-5 rounded-xl w-full bg-slate-50 cursor-pointer"
        @click="toggleCollapse"
      >
        <div class="flex items-center gap-2">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-black font-semibold text-sm"
          >
            5
          </div>
          <h3 class="text-sm font-semibold text-black">Search Filters</h3>
        </div>
        <button
          class="flex justify-between items-center self-stretch my-auto w-5"
          aria-label="Toggle search filters section"
        >
          <div v-html="isCollapsed ? ARROW_DOWN : ARROW_UP"></div>
        </button>
      </div>

      <transition
        name="expand"
        @enter="startExpandTransition"
        @after-enter="endExpandTransition"
        @before-leave="startCollapseTransition"
        @after-leave="endCollapseTransition"
      >
        <div
          class="flex flex-col w-full px-6 pb-5 rounded-xl max-md:px-5 overflow-hidden transition-[height] duration-300 ease-in-out"
          v-if="!isCollapsed"
          ref="collapsible"
        >
          <div class="flex flex-col mb-4 mt-4 text-left pb-4">
            <div class="flex flex-col mb-2">
              <BaseLabel
                forAttr="searchFiltersToggle"
                text="Enable search filters to allow users to filter experts by name, title, and other criteria."
                class="font-inter text-sm font-medium leading-normal tracking-normal mb-2"
              />

              <div class="flex items-center">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="showSearchFilters"
                    @change="toggleSearchFilters"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-purple"
                  ></div>
                  <span class="ml-3 text-sm font-medium text-gray-900">
                    {{ showSearchFilters ? "Enabled" : "Disabled" }}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent } from "vue";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import { ARROW_DOWN, ARROW_UP } from "@/assets/svg/shared/svg-constants";

export default defineComponent({
  name: "SearchFiltersToggle",
  components: {
    BaseLabel,
  },
  props: {
    showSearchFilters: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["searchFiltersChanged"],
  setup(props, { emit }) {
    const isCollapsed = ref(false);
    const collapsible = ref<HTMLElement | null>(null);

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const toggleSearchFilters = () => {
      emit("searchFiltersChanged", !props.showSearchFilters);
    };

    const startExpandTransition = (el: Element) => {
      const element = el as HTMLElement;
      element.style.height = "0";
      void element.offsetHeight; // Force reflow
      element.style.height = `${element.scrollHeight}px`;
    };

    const endExpandTransition = (el: Element) => {
      const element = el as HTMLElement;
      element.style.height = "";
    };

    const startCollapseTransition = (el: Element) => {
      const element = el as HTMLElement;
      element.style.height = `${element.scrollHeight}px`;
      void element.offsetHeight; // Force reflow
      element.style.height = "0";
    };

    const endCollapseTransition = (el: Element) => {
      const element = el as HTMLElement;
      element.style.height = "";
    };

    return {
      isCollapsed,
      collapsible,
      toggleCollapse,
      toggleSearchFilters,
      startExpandTransition,
      endExpandTransition,
      startCollapseTransition,
      endCollapseTransition,
      ARROW_DOWN,
      ARROW_UP,
    };
  },
});
</script>
